import React, { useContext, useMemo, useState } from 'react';
import { QueryClientContext } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui';
import { toast } from 'components/ui/use-toast';
import { FocusPicker, pruneForUseCase } from 'components/auth/org-onboarding';
import {
  ORG_USE_CASES,
  ROLES_BY_USE_CASE,
  normalizeOrgUseCase,
  type AssessArea,
  type GradeBand,
  type OnboardingRole,
  type OrgOnboardingProfile,
  type OrgUseCase,
  type PrimaryGoal,
} from 'modules/auth/onboarding';
import * as Organization from 'modules/organization';
import { TEMPLATES_KEY } from 'modules/templates/hooks';

export interface WorkspaceGoalsOrg {
  id: string;
  useCase?: string;
  onboardingProfile?: OrgOnboardingProfile;
}

interface WorkspaceGoalsCardProps {
  org: WorkspaceGoalsOrg;
  onSaved: (updated: Partial<WorkspaceGoalsOrg>) => void;
}

interface Answers {
  useCase: OrgUseCase | null;
  role: OnboardingRole | null;
  areas: AssessArea[];
  gradeBand: GradeBand | null;
  goal: PrimaryGoal | null;
}

// A legacy use case ('corporate') reads as its current key, so the first save
// rewrites it. Stored answers the use case doesn't offer (e.g. set through the
// API) are dropped here: they could not be shown or deselected otherwise.
const answersOf = (org: WorkspaceGoalsOrg): Answers => {
  const useCase = normalizeOrgUseCase(org.useCase);
  const stored = {
    role: org.onboardingProfile?.role ?? null,
    areas: org.onboardingProfile?.assessAreas ?? [],
    gradeBand: org.onboardingProfile?.gradeBand ?? null,
  };
  return {
    useCase,
    ...(useCase ? pruneForUseCase(useCase, stored) : stored),
    goal: org.onboardingProfile?.primaryGoal ?? null,
  };
};

const sameAnswers = (a: Answers, b: Answers) =>
  a.useCase === b.useCase &&
  a.role === b.role &&
  a.gradeBand === b.gradeBand &&
  a.goal === b.goal &&
  a.areas.length === b.areas.length &&
  a.areas.every((area) => b.areas.includes(area));

/** Edits the onboarding answers that steer template suggestions. */
export function WorkspaceGoalsCard({ org, onSaved }: WorkspaceGoalsCardProps) {
  const { t } = useTranslation();
  const saved = useMemo(() => answersOf(org), [org]);
  const [answers, setAnswers] = useState<Answers>(saved);
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useContext(QueryClientContext);

  const dirty = !sameAnswers(answers, saved);
  const update = (patch: Partial<Answers>) => setAnswers((prev) => ({ ...prev, ...patch }));

  const changeUseCase = (next: OrgUseCase) =>
    setAnswers((prev) => ({ ...prev, useCase: next, ...pruneForUseCase(next, prev) }));

  const handleSave = async () => {
    if (!answers.useCase) return;
    setIsSaving(true);
    try {
      const onboardingProfile = {
        role: answers.role ?? undefined,
        assessAreas: answers.areas.length ? answers.areas : undefined,
        gradeBand: answers.gradeBand ?? undefined,
        primaryGoal: answers.goal ?? undefined,
      };
      const { data } = await Organization.Api.Update(org.id, { useCase: answers.useCase, onboardingProfile });
      onSaved({ useCase: data?.useCase, onboardingProfile: data?.onboardingProfile });
      // Recommendations are ranked from these answers.
      queryClient?.invalidateQueries({ queryKey: [...TEMPLATES_KEY, 'recommended'] });
      toast.success(t('settings.workspace_goals_saved'));
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(t('error'), { description: message || t('something_went_wrong') });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t('settings.workspace_goals_title')}</CardTitle>
        <p className="text-sm text-muted-foreground">{t('settings.workspace_goals_desc')}</p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="workspace-use-case">{t('auth.ob_step_usecase')}</Label>
            <Select value={answers.useCase ?? ''} onValueChange={(v) => changeUseCase(v as OrgUseCase)}>
              <SelectTrigger id="workspace-use-case">
                <SelectValue placeholder={t('auth.ob_usecase_title')} />
              </SelectTrigger>
              <SelectContent>
                {ORG_USE_CASES.map((key) => (
                  <SelectItem key={key} value={key}>
                    {t(`auth.ob_usecase_${key}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="workspace-role">{t('auth.ob_step_role')}</Label>
            <Select
              value={answers.role ?? ''}
              onValueChange={(v) => update({ role: v as OnboardingRole })}
              disabled={!answers.useCase}
            >
              <SelectTrigger id="workspace-role">
                <SelectValue placeholder={t('auth.ob_role_title')} />
              </SelectTrigger>
              <SelectContent>
                {(answers.useCase ? ROLES_BY_USE_CASE[answers.useCase] : []).map((key) => (
                  <SelectItem key={key} value={key}>
                    {t(`auth.ob_jobrole_${key}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {answers.useCase ? (
          <div className="space-y-3">
            <p className="text-sm font-medium text-foreground/90">{t('auth.ob_focus_title')}</p>
            <FocusPicker
              useCase={answers.useCase}
              areas={answers.areas}
              onAreasChange={(areas) => update({ areas })}
              gradeBand={answers.gradeBand}
              onGradeBandChange={(gradeBand) => update({ gradeBand })}
              goal={answers.goal}
              onGoalChange={(goal) => update({ goal })}
            />
          </div>
        ) : null}

        <div className="flex justify-end">
          <Button type="button" onClick={handleSave} disabled={!dirty || !answers.useCase || isSaving}>
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}
            {t('save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default WorkspaceGoalsCard;
